import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
    appendCacheBust,
    bustedReload,
    getPendingUpdateSha,
    markAutoReloadedForSha,
    shouldAutoReloadForSha,
} from '@maat/store-error';

/**
 * Migrates a tab with a pending deploy at the next SAFE moment — a route
 * navigation, where a document load is indistinguishable from the SPA transition
 * and no form state is at risk. ONE automatic reload per sha per tab, so a broken
 * deploy can never reload-loop.
 *
 * PUSH/REPLACE cancel through `history.block` and re-navigate to the same href, so
 * the old build never renders the route first. POP cannot be blocked (history v4
 * rewinds the already-changed URL), so it reloads in place from `history.listen`.
 */
export const UpdateMigrator = () => {
    const history = useHistory();

    useEffect(() => {
        const unblock = history.block((location, action) => {
            if (action === 'POP') return undefined;
            const sha = getPendingUpdateSha();
            if (!sha || !shouldAutoReloadForSha(sha)) return undefined;
            markAutoReloadedForSha(sha);
            // Cache-busted: without ?cb= an immutable-poisoned document is
            // re-served from cache and the migration lands on the old build.
            window.location.assign(appendCacheBust(history.createHref(location)));
            return false;
        });
        return unblock;
    }, [history]);

    useEffect(() => {
        return history.listen((_location, action) => {
            if (action !== 'POP') return;
            const sha = getPendingUpdateSha();
            if (!sha || !shouldAutoReloadForSha(sha)) return;
            markAutoReloadedForSha(sha);
            bustedReload();
        });
    }, [history]);

    return null;
};
